/**
 * 66.1 "Measure first" (lane B): how a team stands in a skirmish — how spread out it is and how many clumps it forms
 * — and how often its enemies are caught in crossfire (hit within a few seconds from directions 60° or more apart).
 * The high ground is the engagements' shooter height advantage (actionDuel engagementCollector). Measurement only:
 * nothing here feeds a decision.
 */
import type { GameState, PlayerState, Vec3 } from '../types';
import { vecNormalize, vecSub, vecDot, vecLength } from '../math';
import { dacos, dcos } from '../core/dmath';
import { personSetOf } from '../core/scaleProfile';
import { PHYS_DT } from '../constants';

/** Teammates closer than this many person lengths are one clump. */
export const CLUMP_LENGTHS = 3;
/** Two hits on one unit this close in time, from bearings this far apart, are crossfire. */
export const CROSSFIRE_SECONDS = 5;
export const CROSSFIRE_DEGREES = 60;

export interface SpreadSummary {
  /** Mean distance from each living unit to its nearest living teammate (person lengths). */
  nearestTeammate: number;
  /** Mean number of clumps per team (single-linkage at CLUMP_LENGTHS), and mean team size, per sample. */
  clumps: number;
  aliveTeam: number;
  /** Share of hits on a unit that came CROSSFIRE_DEGREES or more from another hit on it within CROSSFIRE_SECONDS. */
  crossfire: number;
  hits: number;
}

const arc = (a: Vec3, b: Vec3, r: number) => dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * r;

export function spreadCollector() {
  let nearestSum = 0, nearestN = 0, clumpSum = 0, sizeSum = 0, teamSamples = 0;
  const recent = new Map<number, Array<{ t: number; bearing: Vec3 }>>();
  let hits = 0, crossfire = 0;
  return {
    /** One look at every team (call every quarter second or so). */
    sample(state: GameState): void {
      const ps = personSetOf(state);
      const len = ps.unitRadius * 2;   // a person length
      const teams = new Map<number, PlayerState[]>();
      for (const p of state.players) if (p.alive && !p.airborne) teams.set(p.teamIndex, [...(teams.get(p.teamIndex) ?? []), p]);
      for (const units of teams.values()) {
        if (units.length < 2) continue;
        const r = (u: PlayerState) => state.planets[u.planetIndex].radius;
        const d = (a: PlayerState, b: PlayerState) => a.planetIndex === b.planetIndex ? arc(a.surfaceDir, b.surfaceDir, r(a)) / len : Infinity;
        for (const u of units) {
          let best = Infinity;
          for (const v of units) if (v !== u) best = Math.min(best, d(u, v));
          if (Number.isFinite(best)) { nearestSum += best; nearestN++; }
        }
        // clumps: union-find at CLUMP_LENGTHS
        const parent = units.map((_, i) => i);
        const find = (i: number): number => (parent[i] === i ? i : (parent[i] = find(parent[i])));
        for (let i = 0; i < units.length; i++) for (let j = i + 1; j < units.length; j++) if (d(units[i], units[j]) < CLUMP_LENGTHS) parent[find(i)] = find(j);
        clumpSum += new Set(units.map((_, i) => find(i))).size;
        sizeSum += units.length;
        teamSamples++;
      }
    },
    /** A hit: the target, the shooter, the tick. */
    hit(state: GameState, shooterId: number, targetId: number, tick: number): void {
      const s = state.players.find(p => p.id === shooterId), t = state.players.find(p => p.id === targetId);
      if (!s || !t || s.planetIndex !== t.planetIndex) return;
      const planet = state.planets[t.planetIndex];
      const n = vecNormalize(t.surfaceDir);
      const toShooter = vecSub(vecNormalize(s.surfaceDir), n);
      const tangent = vecSub(toShooter, { x: n.x * vecDot(toShooter, n), y: n.y * vecDot(toShooter, n), z: n.z * vecDot(toShooter, n) });
      if (vecLength(tangent) < 1e-9 || !planet) return;
      const bearing = vecNormalize(tangent);
      const now = tick * PHYS_DT;
      const list = (recent.get(targetId) ?? []).filter(h => now - h.t <= CROSSFIRE_SECONDS);
      hits++;
      const cos = dcos(CROSSFIRE_DEGREES * Math.PI / 180);
      if (list.some(h => vecDot(h.bearing, bearing) <= cos)) crossfire++;
      list.push({ t: now, bearing });
      recent.set(targetId, list);
    },
    summary(): SpreadSummary {
      return {
        nearestTeammate: nearestN ? nearestSum / nearestN : NaN,
        clumps: teamSamples ? clumpSum / teamSamples : NaN,
        aliveTeam: teamSamples ? sizeSum / teamSamples : NaN,
        crossfire: hits ? crossfire / hits : 0,
        hits,
      };
    },
  };
}
